<!-- eslint-disable vue-a11y/form-has-label -->
<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<template>
	<button
		ref="mainElem"
		class="c-ChipMultiSelect"
		:class="ChipMultiSelectClassObject"
		:disabled="disabled"
		@keydown.down.prevent
		@keydown.up.prevent
		@click.self="toggleFocus($event, true)"
		@focus.capture="toggleFocus($event, true)"
		@blur.capture="toggleFocus($event, false)">
		<span
			class="c-ChipMultiSelect__label"
			@click="toggleFocus($event, true)">{{ label }}</span>
		<p
			class="c-ChipMultiSelect__value"
			@click="toggleFocus($event, true)">{{ currValueLabel }}</p>
		<i
			v-show="currValue.length"
			v-if="withClear"
			class="c-ChipMultiSelect__clear"
			@mousedown.prevent
			@click="clearAllSelections(true)" />
		<i
			class="c-ChipMultiSelect__decorator"
			@click="toggleFocus($event, true)" />
		<div
			v-if="isFocused"
			class="c-ChipMultiSelect__mainContainer">
			<span class="c-ChipMultiSelect__innerLabel">{{ label }}</span>
			<div class="c-ChipMultiSelect__inputContainer">
				<InputChip
					v-for="(item, index) in currValue"
					:key="index"
					class="c-ChipMultiSelect__chip"
					:label="item.label"
					:value="item.value"
					:styling-class="item.class"
					:is-all-chip-clickable="true"
					@remove="removeSelection(item, index)"></InputChip>
				<input
					ref="inputElem"
					class="c-ChipMultiSelect__inputText"
					type="text"
					:value="search"
					@input="updateInputModel"
					@keyup.enter="selectFirstOption"
					@keydown.delete="deleteOptions" />
			</div>
			<ProgressBar
				v-if="isDataLoading"
				:height="2"></ProgressBar>
			<ul class="c-ChipMultiSelect__menu">
				<li
					v-for="(opt, index) in availableOptions"
					:key="index"
					class="c-ChipMultiSelect__menuItem">
					<button
						type="button"
						:key-index="index"
						:disabled="!opt.active"
						@click="addSelection(opt, true)"
						@mousedown.prevent>{{ opt.label }}</button>
				</li>
			</ul>
		</div>
	</button>
</template>

<script>
import Vue from 'vue'
import { debounce } from '../../utils/utils'
import InputChip from '../InputChip/InputChip.vue'
import ProgressBar from '../ProgressBar/ProgressBar.vue'

export default {
	name: 'ChipMultiSelect',
	components: {
		InputChip,
		ProgressBar
	},
	props: {
		/**
     * The ChipMultiSelect label
     */
		label: {
			type: String,
			required: true,
			default: () => ''
		},
		/**
     * The ChipMultiSelect options for the dropdown
     */
		selectItems: {
			type: Array,
			required: true,
			default: () => []
		},
		/**
     * The ChipMultiSelect data variable
     */
		data: {
			type: String,
			required: false,
			default: () => undefined
		},
		/**
     * When the **selectItems** are loaded from an external source (XHR request) this option should be set to **true**
		 * @values true, false
     */
		fromExternalSource: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * If the **selectItems** are loaded from **fromExternalSource**, then the ChipMultiSelect can be in a data loading state
		 * @values true, false
     */
		isDataLoading: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * If on **fromExternalSource** mode, the min number of chars that triggers the **search event** can be controlled via this prop
		 * @values true, false
     */
		minCharMatch: {
			type: Number,
			required: false,
			default: () => 3
		},
		/**
     * If the ChipMultiSelect should display a clear input button
     * @values true, false
     */
		withClear: {
			type: Boolean,
			required: false,
			default: () => true
		},
		/**
     * If the ChipMultiSelect is in an active state
     * @values true, false
     */
		isActive: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * If the ChipMultiSelect is disabled (html attribute disabled)
     * @values true, false
     */
		disabled: {
			type: Boolean,
			required: false,
			default: () => false
		}
	},
	data () {
		return {
			selectOptions: undefined,
			preventDuplicateTrigger: false,
			externalDefaultOption: [{
				label: `Type a minimum of ${this.minCharMatch} characters to start search...`,
				active: false
			}],
			externalLoadingOption: [{
				label: 'Working our backend magic...',
				active: false
			}],
			internalNoMoreOptions: [{
				label: 'You have selected them all...',
				active: false
			}],
			internalNotFound: [{
				label: 'Sorry, I can’t find that  :(',
				active: false
			}],
			search: undefined,
			isFocused: false,
			currValue: []
		}
	},
	computed: {
		ChipMultiSelectClassObject () {
			return {
				focus: this.isFocused,
				withValue: this.currValue.length,
				isActive: this.isActive,
				withClear: this.withClear,
				isDataLoading: this.isDataLoading
			}
		},
		availableOptions () {
			const filteredOptions = this.selectOptions.filter(item => item.selectable && item.match)
			if (this.selectOptions.length === 1 && !this.selectOptions[0].active) {
				return this.selectOptions
			} if (!this.fromExternalSource && this.currValue.length && this.currValue.length === this.selectOptions.length) {
				return this.internalNoMoreOptions
			}
			return filteredOptions.length ? filteredOptions : this.internalNotFound

		},
		currValueLabel () {
			return this.currValue.map(item => {
				return item.label
			}).join(', ')
		}
	},
	watch: {
		selectItems: {
			handler (options) {
				if ((this.fromExternalSource && options.length) || !this.fromExternalSource) {
					this.selectOptions = options.map(item => {
						return {
							...item,
							active: true,
							selectable: true,
							match: true
						}
					})
				} else if (this.fromExternalSource && !options.length) {
					this.selectOptions = this.internalNotFound
				}
			},
			immediate: true
		},
		data: {
			handler (newVal) {
				const stringCurrValue = this.currValue.map(item => item.value).join(',')
				if (newVal && newVal !== stringCurrValue) {
					const dataArray = newVal.split(',')

					dataArray.forEach(item => {
						if (this.fromExternalSource && item.indexOf('_***_') !== -1) {
							if (this.currValue.findIndex(curr => curr.value === item.split('_***_')[1]) === -1) {
								this.currValue.push({
									label: item.split('_***_')[0],
									value: item.split('_***_')[1]
								})
							}
						} else {
							const index = this.selectOptions.findIndex(opt => opt.value === item)

							if (index !== -1) {
								this.selectOptions[index].selectable = false
								this.currValue.push(this.selectOptions[index])
							}
						}
					})
				} else if (newVal === null || newVal === undefined) {
					this.clearAllSelections(false)
				}
			},
			immediate: true
		},
		isDataLoading: {
			handler (newVal) {
				if (this.fromExternalSource && newVal &&
        (!this.selectOptions || this.selectOptions.length === 0 ||
        (this.selectOptions.length === 1 && !this.selectOptions[0].active))) {
					this.selectOptions = this.externalLoadingOption
				}
			},
			immediate: true
		}
	},
	created () {
		this.emitSearchValue = debounce(this.emitSearchValue, 350)
	},
	mounted () {
		this.$refs.mainElem.addEventListener('keydown', this.keyDown, true)
	},
	beforeDestroy () {
		this.$refs.mainElem.removeEventListener('keydown', this.keyDown, true)
	},
	methods: {
		toggleFocus (ev, arg) {
			if (!this.disabled) {
				ev.stopPropagation()
				const activeIndex = document.activeElement.getAttribute('key-index')
				this.isFocused = arg

				if (arg && !activeIndex) {
					Vue.nextTick().then(() => {
						this.$refs.inputElem.focus()
					})
				}
			}
		},
		updateInputModel (ev) {
			this.search = ev.target.value
			if (this.fromExternalSource && this.search.length >= this.minCharMatch) {
				this.emitSearchValue(this.search)
			} else if (this.fromExternalSource && this.search.length < this.minCharMatch) {
				this.selectOptions = this.externalDefaultOption
			} else if (!this.fromExternalSource && this.search.length) {
				this.setMatch()
			} else if (!this.fromExternalSource) {
				this.setMatch(true)
			}
		},
		selectFirstOption (ev) {
			if (!this.preventDuplicateTrigger && document.activeElement === ev.target &&
				this.availableOptions && this.availableOptions.length && this.availableOptions[0].active) {
				this.addSelection(this.availableOptions[0])
			} else if (this.preventDuplicateTrigger) {
				this.preventDuplicateTrigger = false
			}
		},
		addSelection (option, fromOption) {
			// prevent adding duplicate items when using external data sources
			if ((this.fromExternalSource && this.currValue.findIndex(item => item.value === option.value) === -1) || !this.fromExternalSource) {
				// set current value
				this.currValue.push({
					value: option.value,
					label: option.label,
					class: option.class
				})
			}

			// remove option as selectable, reset search and reset matching options
			this.search = undefined
			if (this.fromExternalSource) {
				this.selectOptions = this.externalDefaultOption
			} else {
				this.setSelection(option, false)
				this.setMatch(true)
			}

			if (fromOption) {
				this.preventDuplicateTrigger = true
			}
			this.$refs.inputElem.focus()

			// emit current value
			const emitValue = this.currValue.map(item => item.value)
			const emitLabelValue = this.currValue.map(item => `${item.label}_***_${item.value}`)
			/**
			 * Triggers when ChipMultiSelect changes it's value
			 * @event change
			 * @property {string} value new value set
			 */
			// eslint-disable-next-line no-nested-ternary
			this.$emit('change', (this.fromExternalSource && emitLabelValue.length) ? emitLabelValue.join(',') : (!this.fromExternalSource && emitValue.length) ? emitValue.join(',') : undefined)
		},
		removeSelection (option, index) {
			this.currValue.splice(index, 1)

			this.search = undefined
			if (this.fromExternalSource) {
				this.selectOptions = this.externalDefaultOption
			} else {
				this.setSelection(option, true)
				this.setMatch(true)
			}

			const emitValue = this.currValue.map(item => item.value)
			const emitLabelValue = this.currValue.map(item => `${item.label}_***_${item.value}`)
			// eslint-disable-next-line no-nested-ternary
			this.$emit('change', (this.fromExternalSource && emitLabelValue.length) ? emitLabelValue.join(',') : (!this.fromExternalSource && emitValue.length) ? emitValue.join(',') : undefined)
		},
		deleteOptions () {
			if (!this.search && this.currValue.length) {
				if (!this.fromExternalSource) {
					this.setSelection(this.currValue[this.currValue.length - 1], true)
				}

				this.currValue.splice(-1, 1)

				const emitValue = this.currValue.map(item => item.value)
				const emitLabelValue = this.currValue.map(item => `${item.label}_***_${item.value}`)
				// eslint-disable-next-line no-nested-ternary
				this.$emit('change', (this.fromExternalSource && emitLabelValue.length) ? emitLabelValue.join(',') : (!this.fromExternalSource && emitValue.length) ? emitValue.join(',') : undefined)
			}
		},
		clearAllSelections (emitChange) {
			if (!this.disabled) {
				this.currValue = []

				if (this.fromExternalSource) {
					this.selectOptions = this.externalDefaultOption
				} else {
					this.selectOptions.forEach((item, index) => {
						this.selectOptions[index].selectable = true
					})
				}

				if (emitChange) {
					this.$emit('change')
				}
			}
		},
		setSelection (option, val) {
			const index = this.selectOptions.findIndex(item => item.value === option.value)
			if (index !== -1) {
				this.selectOptions[`${index}`].selectable = val
			} else {
				// eslint-disable-next-line no-console
				console.error('option not found', option)
			}
		},
		setMatch (isReset) {
			this.selectOptions.forEach((option, i) => {
				if (isReset) {
					this.selectOptions[i].match = true
				} else {
					const escapedSearchString = this.search.toLowerCase().replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
					const match = option.label.toLowerCase().match(new RegExp(escapedSearchString))
					if (match) {
						this.selectOptions[i].match = true
					} else {
						this.selectOptions[i].match = false
					}
				}
			})
		},
		emitSearchValue (val) {
			/**
			 * Triggers when ChipMultiSelect is on **fromExternalSource** mode and users writes on the search input
			 * @event search
			 * @property {string} val search string value
			 */
			this.$emit('search', val)
		},
		keyDown (ev) {
			const currIndex = document.activeElement.getAttribute('key-index')
			const elemCount = document.querySelector('.c-ChipMultiSelect__menu') ? document.querySelector('.c-ChipMultiSelect__menu').childElementCount : 0

			if (ev.key === 'ArrowDown') {
				let index = 0

				if (currIndex !== null) {
					index = parseInt(currIndex, 10)
					index += 1
				}

				if (index <= elemCount - 1) {
					document.querySelector(`.c-ChipMultiSelect__menu li button[key-index="${index}"]`).focus()
				}
			} else if (ev.key === 'ArrowUp') {
				let index = currIndex !== null ? parseInt(currIndex, 10) : 0
				index -= 1
				if (index >= 0) {
					document.querySelector(`.c-ChipMultiSelect__menu li button[key-index="${index}"]`).focus()
				} else {
					this.$refs.inputElem.focus()
				}
			} else if (ev.key === 'Escape') {
				document.activeElement.blur()
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables.scss';
$padding-right-with-clear: 60px;
$padding-right-without-clear: 40px;

.c-ChipMultiSelect {
  $font-size-label: 16px;
  $font-size-label-top: 12px;
  $font-size-value: 16px;
  $font-size-dropdown: 14px;

  position: relative;
  outline: none;
  height: 50px;
  width: 100%;
  display: block;
  box-sizing: border-box;
  padding: calc(#{$base-padding} / 2) $padding-right-without-clear
    calc(#{$base-padding} / 2) calc(#{$base-padding} / 2);
  color: $un-n700;
  border-radius: $border-radius;
  border: 1px solid $un-gray-light;
  background-color: $un-white;
  cursor: pointer;
  position: relative;
  text-align: left;

  &:disabled {
    border-color: $un-n500;
    background-color: $un-n100;
		cursor: not-allowed;

    & .c-ChipMultiSelect__label,
    & .c-ChipMultiSelect__value {
      color: $un-n500;
    }

    & .c-ChipMultiSelect__decorator {
      background-image: url('data:image/svg+xml,%3Csvg width="12" height="7" viewBox="0 0 12 7" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M5.166 4.21L8.976.4a.795.795 0 0 1 1.125 1.125l-4.409 4.41a.795.795 0 0 1-1.288-.238L.233 1.527A.795.795 0 0 1 1.358.401L5.166 4.21z" fill="%238D98AA" fill-rule="evenodd"/%3E%3C/svg%3E%0A');
    }

    & .c-ChipMultiSelect__clear {
      cursor: not-allowed;
			background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"%3E%3Cpath fill="%238D98AA" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/%3E%3Cpath d="M0 0h24v24H0z" fill="none"/%3E%3C/svg%3E');
    }
  }

  &.focus, &.isActive {
    border: 1px solid $un-blue;
    box-shadow: inset 0 0 0 1px $un-blue;

    .c-ChipMultiSelect__label {
      color: $un-blue;
    }

    & .c-ChipMultiSelect__decorator {
      background-image: url('data:image/svg+xml,%3Csvg width="12" height="7" viewBox="0 0 12 7" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M5.166 4.21L8.976.4a.795.795 0 0 1 1.125 1.125l-4.409 4.41a.795.795 0 0 1-1.288-.238L.233 1.527A.795.795 0 0 1 1.358.401L5.166 4.21z" fill="%233843d0" fill-rule="evenodd"/%3E%3C/svg%3E%0A');
    }
  }

  &.withValue {
    .c-ChipMultiSelect__label {
      top: 5px !important;
      font-size: $font-size-label-top !important;
    }
  }

  &.withClear {
    padding: calc(#{$base-padding} / 2) $padding-right-with-clear
    calc(#{$base-padding} / 2) calc(#{$base-padding} / 2);
  }

  &.isDataLoading {
    .c-ChipMultiSelect__inputContainer {
      border-bottom: none;
    }
  }

  &__label {
    display: block;
    font-family: $primary-font;
    font-size: $font-size-label;
    color: $un-n700;
    position: absolute;
    top: 14px;
    left: calc(#{$base-padding / 2});
    z-index: 1;
    transition: all $default-time $default-easing;
  }

  &__value {
    width: 100%;
    font-size: $font-size-value;
    color: $un-n700;
    letter-spacing: 0.5px;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: calc(#{$base-margin} - 2px);
    opacity: 1;
    transition: opacity $default-time $default-easing;
  }

  &__clear {
    width: 20px;
    height: 20px;
    position: absolute;
    top: 14px;
    right: 36px;
    cursor: pointer;
    background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"%3E%3Cpath fill="%231C357B" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/%3E%3Cpath d="M0 0h24v24H0z" fill="none"/%3E%3C/svg%3E');
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    z-index: 2;

    &:hover {
      background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"%3E%3Cpath fill="%233843d0" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/%3E%3Cpath d="M0 0h24v24H0z" fill="none"/%3E%3C/svg%3E');
    }
  }

  &__decorator {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    content: '';
    display: block;
    width: 42px;
    pointer-events: none;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url('data:image/svg+xml,%3Csvg width="12" height="7" viewBox="0 0 12 7" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M5.166 4.21L8.976.4a.795.795 0 0 1 1.125 1.125l-4.409 4.41a.795.795 0 0 1-1.288-.238L.233 1.527A.795.795 0 0 1 1.358.401L5.166 4.21z" fill="%23343F50" fill-rule="evenodd"/%3E%3C/svg%3E%0A');
  }

  &__mainContainer {
    position: absolute;
    width: calc(100% + 2px);
    top: -1px;
    left: -1px;
    z-index: 5;
    background-color: $un-white;
    border-radius: $border-radius;
    border: 2px solid $un-blue;
    box-shadow: 0px 3px 6px 0px rgba(174, 185, 203, 0.6), 0px 3px 6px 0px rgba(174, 185, 203, 0.6);
  }

  &__innerLabel {
    display: block;
    font-family: $primary-font;
    font-size: $font-size-label-top;
    color: $un-blue;
    margin-top: 5px;
    margin-left: calc(#{$base-margin / 2});
  }

  &__inputContainer {
    width: 100%;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding: 0 calc(#{$base-padding} / 2) calc(#{$base-padding} / 2) calc(#{$base-padding} / 2);
    border-bottom: 2px solid $un-n200;
  }

  &__chip {
    margin-right: calc(#{$base-margin} / 2);
    margin-top: calc(#{$base-margin} / 2);
  }

  &__inputText {
    width: 160px;
    height: 30px;
    outline: none;
    border: none;
    background-color: transparent;
    font-family: $primary-font;
    font-size: $font-size-value;
    line-height: $font-size-value;
    color: $un-n700;
    margin-top: calc(#{$base-margin} / 2);
    box-sizing: border-box;
  }

  &__menu {
    list-style: none;
    margin: 0;
    padding: 0;
    min-width: 100%;
    max-height: calc(48px * 4);
    overflow-x: hidden;
    overflow-y: auto;
  }

  &__menuItem {
    margin: 0;
    padding: 0;

    button {
      width: 100%;
      height: 100%;
      background-color: transparent;
      color: $un-gray-bd;
      border: none;
      min-height: 48px;
      padding: $base-padding;
      cursor: pointer;
      font-size: $font-size-dropdown;
      text-align: left;
      outline: none;

      &::-moz-focus-inner {
        border: 0;
      }

      &:disabled {
        background-color: transparent !important;
        cursor: default !important;
        font-style: italic !important;
        color: $un-n600 !important;
      }

      &:hover, &:focus {
        background-color: $un-blue-light;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// node_modules/@unbabel/ui/src/components/ChipMultiSelect/ChipMultiSelect.vue